import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

const ITEMS_PER_PAGE = 50;

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

  // 30 = location number, 27 = region, 10 = location code
  // 15 = address, 7 = issue type, 32 = follow up date/time
  // 18 = reported by, 19 = status, 26 = notes

export const ServiceTickets = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logoutUser = () => {
    dispatch({ type: 'LOG_OUT', payload: null });
    sessionStorage.setItem('currentUser', null);
    window.location = "/";
  };
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filterTickets = (data, term) => {
    if (!term) return data;
    return data.filter(item => {
      const searchStringticketCode = item && item[10] ? item[10].value : "";
      const searchStringAddress = item && item[15] ? item[15].value : "";
      const searchStringRequestedBy = item && item[18] ? item[18].value : "";
      const searchStringIssueType = item && item[7] ? item[7].value : "";
      const status = item && item[21] ? item[21].value : null;
      const statusText = !!status ? "Complete" : "Pending";
      return searchStringticketCode.toLowerCase().includes(term) ||
        searchStringAddress.toLowerCase().includes(term) ||
        searchStringRequestedBy.toLowerCase().includes(term) ||
        statusText.toLowerCase().includes(term) ||
        searchStringIssueType.toLowerCase().includes(term);
    });
  };

  useEffect(() => {
    let isMounted = true;

    const fetchInitial = async () => {
      setIsLoading(true);
      const response = await fetch("/apiserviceTickets?limit=50", { credentials: 'include' });
      const data = await response.json();
      if (data && data.sessionExpired) { logoutUser(); return; }
      if (!isMounted) return;
      setTickets(data);
      setFilteredTickets(data);
      setIsLoading(false);

      // Phase 2: fetch all records in background
      const allResponse = await fetch("/apiserviceTickets", { credentials: 'include' });
      const allData = await allResponse.json();
      if (allData && allData.sessionExpired) { logoutUser(); return; }
      if (!isMounted) return;
      setTickets(allData);
      setFilteredTickets(searchTerm ? filterTickets(allData, searchTerm) : allData);
      setIsFullyLoaded(true);
    };

    fetchInitial();
    return () => { isMounted = false; };
  }, []);

  const handleChangeInput = (e) => {
    const term = (e.target.value).toLowerCase();
    setSearchTerm(term);
    setFilteredTickets(filterTickets(tickets, term));
    setCurrentPage(1);
  };

  const redirectToServiceTicketPage = (e, ticketId) => {
    navigate(`/serviceTicket/${ticketId}`, {state: {id: ticketId}});
  };

  const handleRedirectCreateTicket = () => {
    navigate("/createTicket");
  };

  // Pagination
  const totalPages = Math.ceil(filteredTickets.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTickets = filteredTickets.slice(startIndex, endIndex);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredTickets.length / ITEMS_PER_PAGE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredTickets, currentPage]);

  const headers = (
    <tr>
      <th>Client Region</th>
      <th>Location Code</th>
      <th>Address</th>
      <th>Issue Type</th>
      <th>Reported By</th>
      <th>Status</th>
      <th>Notes</th>
    </tr>
  );

  return (
    <div className="tickets">
    <div className="ticketsList">
      <h2>White Castle tickets</h2>
      <div className="ticketsActionsWrapper">
        <div className="ticketsInputWrapper">
          <input placeholder="Search requests..." onChange={handleChangeInput}></input>
        </div>
      <button onClick={handleRedirectCreateTicket} className="mainButton">+ Create Service Request</button>
      </div>
      {isLoading ? (
        <div className="skeleton-wrapper">
          <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
            Loading tickets...
          </p>
          <table className="skeleton-table">
            <tbody>
              {headers}
              {Array.from({ length: 15 }).map((_, idx) => (
                <tr key={idx}>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-long"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-medium"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-medium"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-short"></div></td>
                  <td className="tableCell"><div className="skeleton-cell skeleton-cell-long"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <p style={{color: '#666', fontSize: '14px', marginBottom: '10px', marginTop: '10px'}}>
            Showing {filteredTickets.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredTickets.length)} of {filteredTickets.length} tickets
            {!isFullyLoaded && <span> (loading more...)</span>}
          </p>
          <table id="tableWrapper">
          <tbody>
          {headers}
            {paginatedTickets && paginatedTickets.length > 0 && paginatedTickets.map((ticket, idx) => {
              const ticketId = ticket && ticket[3] ? ticket[3].value : null;
              const status = ticket && ticket[21] ? ticket[21].value : null;
              const statusText = !!status ? "Complete" : "Pending";
              const statusColor = !!status ? "LimeGreen" : "Orchid";
              return (
                <tr key={idx} onClick={(e) => redirectToServiceTicketPage(e, ticketId)} className="tableRow">
                  <td className="tableCell">{ticket && ticket[27] ? ticket[27].value : ""}</td>
                  <td className="tableCell">{ticket && ticket[10] ? ticket[10].value : ""}</td>
                  <td className="tableCell">{ticket && ticket[15] ? ticket[15].value : ""}</td>
                  <td className="tableCell">{ticket && ticket[7] ? ticket[7].value : ""}</td>
                  <td className="tableCell">{ticket && ticket[18] ? ticket[18].value : ""}</td>
                  <td className="tableCell"><p className="status-badge-dynamic" style={{backgroundColor: statusColor}}>{statusText}</p></td>
                  <td className="tableCell">{ticket && ticket[26] ? ticket[26].value : ""}</td>
                </tr>
              )
            })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="pagination-wrapper">
              <button
                className={`pagination-btn ${currentPage === 1 ? 'pagination-btn--disabled' : ''}`}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              {getPageNumbers(currentPage, totalPages).map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} className="pagination-btn pagination-btn--disabled">...</span>
                ) : (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'pagination-btn--active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                className={`pagination-btn ${currentPage === totalPages ? 'pagination-btn--disabled' : ''}`}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
    </div>
    )
}
